import { getTray, getTrayComments } from '@/js/core/utils/trays-api';

// Long enough to cover hover -> click, short enough that a tray opened much later
// re-reads rather than showing comments that went stale while the page sat idle.
const PREFETCH_TTL_MS = 60000;

// Hover intent: a pointer sweeping across a grid should not warm every card it crosses.
const HOVER_INTENT_MS = 200;

/** @type {Map<string, {request: Promise<object|null>, expiresAt: number}>} */
const warmed = new Map();

let pendingTimeoutId = null;

/**
 * Read the tray detail payload the view needs.
 *
 * The comments read is started alongside the tray read but awaited after it, so an
 * unreadable tray still reports its own failure rather than whichever call happened
 * to reject first.
 *
 * @param {string} uuid - The tray UUID
 * @returns {Promise<{tray: object, comments: object[], meta: object|null}>}
 */
async function fetchTrayPayload(uuid) {
  const trayRequest = getTray(uuid);
  const commentsRequest = getTrayComments(uuid);

  // The tray read can reject first and short-circuit the await below, which would
  // leave the comments rejection unhandled.
  commentsRequest.catch(() => {});

  const trayResult = await trayRequest;
  const commentsResult = await commentsRequest;

  return {
    tray: trayResult.data,
    comments: commentsResult.data,
    meta: commentsResult.meta?.pagination || null,
  };
}

/**
 * Warm the tray detail payload so opening the tray does not wait on the network.
 *
 * Failures resolve to null rather than rejecting: a warm-up the user never asked
 * for must not surface an error, and the click-time fetch will report the real
 * failure through its own handling.
 *
 * @param {string|null} uuid - The tray UUID
 */
export function prefetchTray(uuid) {
  if (!uuid) return;

  const now = Date.now();

  // Hovers that never became clicks are never consumed, so expiry is the only thing
  // that clears them.
  warmed.forEach((entry, key) => {
    if (entry.expiresAt <= now) warmed.delete(key);
  });

  if (warmed.has(uuid)) return;

  warmed.set(uuid, {
    request: fetchTrayPayload(uuid).catch(() => null),
    expiresAt: now + PREFETCH_TTL_MS,
  });
}

/**
 * Warm a tray once the pointer has rested on it. Only one element can be hovered
 * at a time, so a single shared timer is enough.
 *
 * @param {string|null} uuid - The tray UUID
 */
export function scheduleTrayPrefetch(uuid) {
  cancelTrayPrefetch();

  if (!uuid) return;

  pendingTimeoutId = setTimeout(() => {
    pendingTimeoutId = null;
    prefetchTray(uuid);
  }, HOVER_INTENT_MS);
}

/**
 * Drop a hover warm-up that has not fired yet.
 */
export function cancelTrayPrefetch() {
  if (pendingTimeoutId === null) return;

  clearTimeout(pendingTimeoutId);
  pendingTimeoutId = null;
}

/**
 * Read the tray detail payload, using a warm entry when one is still valid.
 *
 * The warm entry is consumed on read, so a later visit re-reads rather than replaying
 * comments that have since moved on, and a warm-up that failed falls through to a
 * fresh read that can report the failure properly.
 *
 * @param {string|null} uuid - The tray UUID
 * @returns {Promise<{tray: object, comments: object[], meta: object|null}>}
 */
export async function loadTrayPayload(uuid) {
  const entry = uuid ? warmed.get(uuid) : null;

  if (!entry) return fetchTrayPayload(uuid);

  warmed.delete(uuid);

  const warmPayload = entry.expiresAt > Date.now() ? await entry.request : null;

  return warmPayload ?? fetchTrayPayload(uuid);
}
